import _ from "lodash";

import { createTheme, ThemeOptions } from "@mui/material/styles";

import { darkPalette, palette } from "./palette";

const defaultTheme = createTheme();

const sharedOptions = {
    spacing: 4,
    typography: {
        fontFamily: "Inter Variable, Noto Sans JP Variable",
        fontWeightLight: 300,
        fontWeightRegular: 300,
        fontWeightMedium: 500,
        fontWeightBold: 700,
        button: {
            fontWeight: 300,
        },
    },
    components: {
        MuiTab: {
            styleOverrides: {
                root: {
                    minHeight: 50,
                    height: 50,
                    textTransform: "none",
                    "&.Mui-selected": {
                        fontWeight: 600,
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                contained: {
                    textTransform: "none",
                    boxShadow: defaultTheme.shadows[2],

                    "&:hover": {
                        filter: "brightness(0.92)",
                        boxShadow: defaultTheme.shadows[4],
                    },
                    "&:disabled": {
                        boxShadow: "none",
                    },
                },

                containedPrimary: {
                    boxShadow: defaultTheme.shadows[2],

                    "&:hover": {
                        filter: "brightness(0.92)",
                        boxShadow: defaultTheme.shadows[4],
                    },
                },

                containedSecondary: {
                    boxShadow: defaultTheme.shadows[2],

                    "&:hover": {
                        filter: "brightness(0.92)",
                        boxShadow: defaultTheme.shadows[4],
                    },
                },

                outlined: {
                    textTransform: "none",
                    boxShadow: defaultTheme.shadows[2],

                    "&:hover": {
                        boxShadow: defaultTheme.shadows[4],
                        filter: "brightness(0.96)",
                    },
                    "&:disabled": {
                        boxShadow: "none",
                    },
                },

                outlinedPrimary: {
                    boxShadow: defaultTheme.shadows[2],

                    "&:hover": {
                        boxShadow: defaultTheme.shadows[4],
                        filter: "brightness(0.96)",
                    },
                },
            },

            defaultProps: {
                disableRipple: true,
            },
        },
    },
};

const lightThemeOptions = _.merge<object, Partial<ThemeOptions>, Partial<ThemeOptions>>({}, sharedOptions, {
    palette: {
        mode: "light",
        success: { main: palette.green },
        emteria: {
            primary: palette.bluePrimary,
            action: palette.blueAccent,
            link: palette.blueAccent,
            success: palette.green,
            error: palette.red,
            warning: palette.orange,
            background: {
                cardHeader: palette.cardHeader,
                default: palette.gray50,
                paper: palette.white,
                paperDeep: palette.gray100,
                disabled: palette.gray400,
                inputDisabled: palette.gray50,
                tableHeaders: palette.blueLight,
                tableCells: palette.gray50,
                input: palette.white,
                listHover: palette.gray100,
                selectedRow: palette.blueLight,
                navigationHover: palette.blueLight,
            },
            text: {
                primary: palette.black,
                secondary: palette.gray600,
                inverse: palette.white,
            },
            border: {
                primary: palette.gray300,
                secondary: palette.blackTransparent,
            },
            alerts: {
                info: { bg: "#F3F8FE", border: "#D6EEFF", iconColor: "#1976D2" },
                warning: { bg: "#FFFBF0", border: "#FFE8C5", iconColor: "#ED6C02" },
                error: { bg: "#FFF5F5", border: "#FFD6D6", iconColor: "#D32F2F" },
            },
        },
    },
    components: {
        MuiDrawer: {
            styleOverrides: {
                paper: { backgroundColor: palette.gray100 },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: { backgroundColor: palette.white },
            },
        },
        MuiButton: {
            styleOverrides: {
                contained: {
                    borderColor: palette.red,
                    backgroundColor: palette.red,
                    color: palette.white,

                    "&:hover": {
                        borderColor: palette.red,
                        backgroundColor: palette.red,
                        color: palette.white,
                    },
                    "&:disabled": {
                        borderColor: palette.gray300,
                        backgroundColor: palette.gray300,
                    },
                },

                containedPrimary: {
                    backgroundColor: palette.blueAccent,
                    borderColor: palette.blueAccent,
                    color: palette.white,

                    "&:hover": {
                        backgroundColor: palette.blueAccent,
                        borderColor: palette.blueAccent,
                        color: palette.white,
                    },
                },

                containedSecondary: {
                    borderColor: palette.white,
                    backgroundColor: palette.white,
                    color: palette.gray600,

                    "&:hover": {
                        borderColor: palette.white,
                        backgroundColor: palette.white,
                        color: palette.gray600,
                    },
                },

                outlined: {
                    backgroundColor: palette.white,
                    borderColor: palette.red,
                    color: palette.red,

                    "&:hover": {
                        borderColor: palette.red,
                        color: palette.red,
                    },
                    "&:disabled": {
                        borderColor: palette.gray300,
                        backgroundColor: palette.gray300,
                    },
                },

                outlinedPrimary: {
                    backgroundColor: palette.white,
                    borderColor: palette.blueAccent,
                    color: palette.blueAccent,

                    "&:hover": {
                        backgroundColor: palette.white,
                        color: palette.blueAccent,
                        borderColor: palette.blueAccent,
                    },
                },
            },
        },
    },
});

const darkThemeOptions = _.merge<object, Partial<ThemeOptions>, Partial<ThemeOptions>>({}, sharedOptions, {
    palette: {
        mode: "dark",
        success: { main: darkPalette.green },
        emteria: {
            primary: darkPalette.bluePrimary,
            action: darkPalette.blueAccent,
            link: darkPalette.bluePrimary,
            success: darkPalette.green,
            error: darkPalette.red,
            warning: darkPalette.orange,
            background: {
                cardHeader: darkPalette.cardHeader,
                default: darkPalette.black,
                paper: darkPalette.gray50,
                paperDeep: darkPalette.gray100,
                disabled: darkPalette.gray400,
                inputDisabled: darkPalette.gray400,
                tableHeaders: darkPalette.cardHeader,
                tableCells: darkPalette.gray700,
                input: darkPalette.inputBackground,
                listHover: darkPalette.blackTransparent,
                selectedRow: darkPalette.blueLightTransparent,
                navigationHover: darkPalette.gray700,
            },
            text: {
                primary: darkPalette.white,
                secondary: darkPalette.white,
                inverse: darkPalette.black,
            },
            border: {
                primary: darkPalette.gray300,
                secondary: darkPalette.blackTransparent,
            },
            alerts: {
                info: {
                    bg: "rgba(123, 163, 212, 0.15)",
                    border: "rgba(123, 163, 212, 0.35)",
                    iconColor: darkPalette.bluePrimary,
                },
                warning: {
                    bg: "rgba(217, 149, 49, 0.15)",
                    border: "rgba(217, 149, 49, 0.35)",
                    iconColor: darkPalette.orange,
                },
                error: {
                    bg: "rgba(191, 76, 54, 0.15)",
                    border: "rgba(191, 76, 54, 0.35)",
                    iconColor: darkPalette.red,
                },
            },
        },
    },
    components: {
        MuiDialog: {
            styleOverrides: {
                paper: {
                    backgroundColor: darkPalette.gray50,
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    backgroundColor: darkPalette.black,
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    backgroundColor: darkPalette.gray50,
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    backgroundColor: darkPalette.gray50,
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: { backgroundColor: darkPalette.gray100 },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                colorSecondary: {
                    "&$checked": {
                        color: darkPalette.blueAccent,
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                contained: {
                    borderColor: darkPalette.red,
                    backgroundColor: darkPalette.red,
                    color: darkPalette.white,

                    "&:hover": {
                        borderColor: darkPalette.red,
                        backgroundColor: darkPalette.red,
                        color: darkPalette.white,
                    },
                    "&:disabled": {
                        borderColor: darkPalette.gray400,
                        backgroundColor: darkPalette.gray400,
                    },
                },

                containedPrimary: {
                    backgroundColor: darkPalette.primaryButton,
                    borderColor: darkPalette.primaryButton,
                    color: darkPalette.white,

                    "&:hover": {
                        backgroundColor: darkPalette.primaryButtonHover,
                        borderColor: darkPalette.primaryButtonHover,
                        color: darkPalette.white,
                    },
                },

                containedSecondary: {
                    backgroundColor: darkPalette.secondaryButton,
                    borderColor: darkPalette.secondaryButton,
                    color: darkPalette.white,

                    "&:hover": {
                        backgroundColor: darkPalette.secondaryButtonHover,
                        borderColor: darkPalette.secondaryButtonHover,
                        color: darkPalette.white,
                        filter: "none",
                    },
                },

                containedError: {
                    backgroundColor: darkPalette.dangerButton,
                    borderColor: darkPalette.dangerButton,
                    color: darkPalette.white,

                    "&:hover": {
                        backgroundColor: darkPalette.dangerButtonHover,
                        borderColor: darkPalette.dangerButtonHover,
                        color: darkPalette.white,
                        filter: "none",
                    },
                },

                outlined: {
                    textTransform: "none",
                    backgroundColor: darkPalette.white,
                    borderColor: darkPalette.red,
                    color: darkPalette.red,

                    "&:hover": {
                        borderColor: darkPalette.red,
                        color: darkPalette.red,
                    },
                    "&:disabled": {
                        borderColor: darkPalette.gray400,
                        backgroundColor: darkPalette.gray400,
                    },
                },

                outlinedPrimary: {
                    backgroundColor: darkPalette.gray700,
                    borderColor: darkPalette.bluePrimary,
                    color: darkPalette.bluePrimary,

                    "&:hover": {
                        backgroundColor: darkPalette.gray700,
                        color: darkPalette.bluePrimary,
                        borderColor: darkPalette.bluePrimary,
                        filter: "brightness(1.1)",
                    },
                },

                outlinedError: {
                    backgroundColor: darkPalette.gray700,
                    borderColor: darkPalette.red,
                    color: darkPalette.red,

                    "&:hover": {
                        backgroundColor: darkPalette.gray700,
                        borderColor: darkPalette.red,
                        color: darkPalette.red,
                        filter: "brightness(1.1)",
                    },
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                standardWarning: {
                    backgroundColor: darkPalette.gray700,
                    color: darkPalette.white,
                    borderLeft: `4px solid ${darkPalette.orange}`,
                    "& .MuiAlert-icon": {
                        color: darkPalette.orange,
                    },
                },
                filledWarning: {
                    backgroundColor: darkPalette.gray700,
                    color: darkPalette.white,
                    borderLeft: `4px solid ${darkPalette.orange}`,
                    "& .MuiAlert-icon": {
                        color: darkPalette.orange,
                    },
                },
                standardInfo: {
                    backgroundColor: darkPalette.gray700,
                    color: darkPalette.white,
                    borderLeft: `4px solid ${darkPalette.bluePrimary}`,
                    "& .MuiAlert-icon": {
                        color: darkPalette.bluePrimary,
                    },
                },
                filledInfo: {
                    backgroundColor: darkPalette.gray700,
                    color: darkPalette.white,
                    borderLeft: `4px solid ${darkPalette.bluePrimary}`,
                    "& .MuiAlert-icon": {
                        color: darkPalette.bluePrimary,
                    },
                },
                standardError: {
                    backgroundColor: darkPalette.gray700,
                    color: darkPalette.white,
                    borderLeft: `4px solid ${darkPalette.red}`,
                    "& .MuiAlert-icon": {
                        color: darkPalette.red,
                    },
                },
                filledError: {
                    backgroundColor: darkPalette.gray700,
                    color: darkPalette.white,
                    borderLeft: `4px solid ${darkPalette.red}`,
                    "& .MuiAlert-icon": {
                        color: darkPalette.red,
                    },
                },
                standardSuccess: {
                    backgroundColor: darkPalette.gray700,
                    color: darkPalette.white,
                    borderLeft: `4px solid ${darkPalette.green}`,
                    "& .MuiAlert-icon": {
                        color: darkPalette.green,
                    },
                },
                filledSuccess: {
                    backgroundColor: darkPalette.gray700,
                    color: darkPalette.white,
                    borderLeft: `4px solid ${darkPalette.green}`,
                    "& .MuiAlert-icon": {
                        color: darkPalette.green,
                    },
                },
            },
        },
    },
});

export const darkTheme = createTheme(darkThemeOptions);
export const lightTheme = createTheme(lightThemeOptions);
